// src/schema/product.js
import { z } from "zod";

// URL http/https OU caminho local /assets
const urlOrAssetPath = z
  .string()
  .trim()
  .refine(
    (v) =>
      /^https?:\/\/.+/i.test(v) ||
      /^\/assets\/.+/i.test(v) ||
      /^\/?.+\.(png|jpg|jpeg|webp|gif)$/i.test(v),
    "Informe uma URL válida (http/https) ou caminho relativo de imagem"
  );

// Schema principal para o formulário
export const productFormSchema = z.object({
  // Mesmas regras do validateProduct da API (ERRC081)
  name: z
    .string()
    .trim()
    .min(3, "Informe o nome (mínimo 3 caracteres)")
    .max(150, "Nome deve ter no máximo 150 caracteres"),
  description: z.string().trim().min(1, "Descrição obrigatória"),
  category: z.string().trim().min(1, "Categoria obrigatória"),
  price: z.coerce
    .number({ invalid_type_error: "Preço deve ser numérico" })
    .positive("Preço deve ser maior que zero"),
  image_url: urlOrAssetPath,
  available: z.boolean().default(true),

  // checkbox
  creatingFlag: z.boolean().default(false),
  hasExtraProductsFlag: z.boolean().default(false),
  hasExtraIngredientsFlag: z.boolean().default(false),
  combo: z.boolean().default(false),

  // campos condicionais
  unid: z.string().optional().default(""),
  weight: z.coerce.number().int().nonnegative().optional().default(0),
  unid_product: z.string().optional().default(""),
  weight_product: z.coerce.number().int().nonnegative().optional().default(0),
  totalIngredients: z.coerce.number().int().nonnegative().optional().default(0),

  totalProductsSelects: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .default(0),

  min_stock: z.coerce.number().int().nonnegative().optional().default(0),

  // ingredientes selecionados
  ingredientsSelectedIds: z.array(z.string()).optional().default([]),
  productsSelectedCodes: z.array(z.string()).optional().default([]),

  // adicionais que o cliente pode escolher (extraIngredients / extraProducts)
  extraIngredientsSelectedIds: z.array(z.string()).optional().default([]),
  extraProductsSelectedCodes: z.array(z.string()).optional().default([]),
});

const ids = (list, key) =>
  Array.isArray(list)
    ? list
        .map((item) => item?.[key])
        .filter((v) => v != null && v !== "")
        .map(String)
    : [];

/**
 * Converte o produto completo da API (GET /products/{id}) nos valores do form.
 */
export function productToFormValues(product) {
  return {
    name: product.name ?? "",
    description: product.description ?? "",
    price: Number(product.price ?? 0),
    category: product.category ?? "",
    image_url: product.image_url ?? "",
    available:
      typeof product.available === "boolean" ? product.available : true,
    min_stock: Number(product.min_stock ?? 0),

    creatingFlag: (product.creating ?? "N") === "S",
    combo: (product.combo ?? "N") === "S",

    hasExtraIngredientsFlag: (product.hasExtraIngredients ?? "N") === "S",
    totalIngredients: Number(product.totalIngredients ?? 0),
    weight: Number(product.weight ?? 0),
    unid: product.unid ?? "",
    ingredientsSelectedIds: ids(product.ingredients, "id"),
    extraIngredientsSelectedIds: ids(product.extraIngredients, "id"),

    hasExtraProductsFlag: (product.hasExtraProducts ?? "N") === "S",
    totalProductsSelects: Number(product.totalProductsSelects ?? 0),
    weight_product: Number(product.weight_product ?? 0),
    unid_product: product.unid_product ?? "",
    productsSelectedCodes: ids(product.products, "code"),
    extraProductsSelectedCodes: ids(product.extraProducts, "code"),
  };
}

/**
 * Converter os valores do form em payload para a API.
 * ingredientsCatalog: [{ id, name, price }] para montar `ingredients`
 * (a API descarta ingrediente sem `name`).
 */
export function toProductPayload(formValues, ingredientsCatalog = []) {
  const hasIngredients = Boolean(formValues.hasExtraIngredientsFlag);
  const hasProducts = Boolean(formValues.hasExtraProductsFlag);

  const mapById = new Map(
    (ingredientsCatalog || []).map((i) => [String(i.id), i])
  );
  const ingredients = hasIngredients
    ? (formValues.ingredientsSelectedIds || [])
        .map((id) => mapById.get(String(id)))
        .filter(Boolean)
        .map((i) => ({
          id: String(i.id),
          name: String(i.name ?? ""),
          price: Number(i.price ?? 0),
        }))
    : [];

  const products = hasProducts
    ? (formValues.productsSelectedCodes || []).map((code) => ({
        code: String(code),
      }))
    : [];

  // A API grava só o `id` do ingrediente e o `code` do produto extra.
  // Com o flag "N" a API apaga os extras; com "S" a lista substitui a atual.
  const extraIngredients = hasIngredients
    ? (formValues.extraIngredientsSelectedIds || []).map((id) => ({
        id: String(id),
      }))
    : [];
  const extraProducts = hasProducts
    ? (formValues.extraProductsSelectedCodes || []).map((code) => ({
        code: String(code),
      }))
    : [];

  return {
    name: formValues.name,
    description: formValues.description || "",
    category: formValues.category,
    price: formValues.price,
    image_url: formValues.image_url,
    available: formValues.available ?? true,
    creating: formValues.creatingFlag ? "S" : "N",
    // unid e unid_product são sempre enviados (sem eles o salvamento falha)
    unid: hasIngredients ? (formValues.unid || "").trim() : "",
    weight: hasIngredients ? formValues.weight : 0,
    totalIngredients: hasIngredients ? formValues.totalIngredients : 0,
    unid_product: hasProducts ? (formValues.unid_product || "").trim() : "",
    weight_product: hasProducts ? formValues.weight_product : 0,
    totalProductsSelects: hasProducts ? formValues.totalProductsSelects : 0,
    hasExtraIngredients: hasIngredients ? "S" : "N",
    hasExtraProducts: hasProducts ? "S" : "N",
    combo: formValues.combo ? "S" : "N",
    min_stock: formValues.min_stock || 0,
    ingredients,
    products,
    extraIngredients,
    extraProducts,
  };
}
